import { styled } from "goober";
import {
  type DragEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { Box } from "../../Box";
import { Input } from "../../Input";
import { Text } from "../../Text";
import type { SsoConfig } from "./constants";
import {
  CertTextarea,
  Field,
  FieldActions,
  FieldLink,
  HelperRow,
  MonoTextarea,
} from "./shared";

const DropZone = styled("div")`
  position: relative;
  border-radius: 8px;
  &[data-dragging="true"] textarea {
    outline: 0.5px solid var(--control-bg);
    box-shadow: 0 0 0 3px var(--border-alpha);
  }
`;

function ManualField({
  label,
  action,
  hint,
  error,
  children,
}: {
  label: string;
  action?: ReactNode;
  hint: string;
  error?: string | null;
  children: ReactNode;
}) {
  return (
    <Field>
      <Box horizontal={8} vertical={10}>
        <Text variant="text-small-md">{label}</Text>
      </Box>
      {children}
      <HelperRow>
        <Text variant="text-small" color="var(--text-secondary)">
          {hint}
        </Text>
        {action && <FieldActions>{action}</FieldActions>}
      </HelperRow>
      {error && (
        <Box horizontal={8} top={6}>
          <Text variant="text-small" color="var(--text-error)">
            {error}
          </Text>
        </Box>
      )}
    </Field>
  );
}

function looksLikePemCert(text: string): boolean {
  const body = text
    .replace(/-----(BEGIN|END) CERTIFICATE-----/g, "")
    .replace(/\s/g, "");
  return body.length > 0 && /^[A-Za-z0-9+/=]+$/.test(body);
}

type FieldsProps = {
  config: SsoConfig;
  onConfigChange: (updater: (prev: SsoConfig) => SsoConfig) => void;
  existing?: boolean;
};

function metadataKind(text: string): "url" | "xml" | "invalid" | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith("<")) return "xml";
  return /^https:\/\//i.test(trimmed) ? "url" : "invalid";
}

const INVALID_METADATA = "Paste an https:// metadata URL or the metadata XML.";

// One field takes the IdP’s metadata however the IdP hands it over: a URL we
// fetch on save, or the XML itself, pasted or dropped as a file.
export function MetadataField({
  config,
  onConfigChange,
  existing,
  label,
  placeholder,
  onEnterManually,
}: FieldsProps & {
  label: string;
  placeholder: string;
  onEnterManually: () => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const configValue = config.metadataXml || config.metadataUrl;
  const [text, setText] = useState(configValue);
  const [syncedValue, setSyncedValue] = useState(configValue);
  if (configValue !== syncedValue) {
    setSyncedValue(configValue);
    setText(configValue);
  }
  const [fileError, setFileError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const kind = metadataKind(text);

  useEffect(() => {
    textareaRef.current?.setCustomValidity(
      kind === "invalid" ? INVALID_METADATA : ""
    );
  }, [kind]);

  function ingest(value: string) {
    const trimmed = value.trim();
    setText(value);
    setSyncedValue(trimmed);
    setFileError(null);
    onConfigChange((f) =>
      metadataKind(trimmed) === "xml"
        ? { ...f, metadataXml: trimmed, metadataUrl: "" }
        : { ...f, metadataUrl: trimmed, metadataXml: "" }
    );
  }

  async function ingestFile(file: File | undefined) {
    if (!file) return;
    const isXml =
      file.type.includes("xml") || file.name.toLowerCase().endsWith(".xml");
    if (!isXml) {
      setFileError("Only XML files are supported.");
      return;
    }
    ingest(await file.text());
  }

  const status =
    fileError ??
    (kind === "invalid"
      ? INVALID_METADATA
      : kind === "url"
        ? "We’ll fetch this metadata when you save."
        : kind === "xml"
          ? "Metadata XML added."
          : null);

  return (
    <Field>
      <Box horizontal={8} vertical={10}>
        <Text variant="text-small-md">{label}</Text>
      </Box>
      <DropZone
        data-dragging={dragging}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e: DragEvent<HTMLDivElement>) => {
          e.preventDefault();
          setDragging(false);
          void ingestFile(e.dataTransfer.files[0]);
        }}
      >
        <MonoTextarea
          ref={textareaRef}
          aria-label={label}
          value={text}
          onChange={(e) => ingest(e.target.value)}
          placeholder={existing ? "Leave blank to keep current" : placeholder}
          required={!existing}
          style={{ minHeight: kind === "xml" ? 96 : 56 }}
        />
      </DropZone>
      <HelperRow>
        {status && (
          <Text
            variant="text-small"
            color={
              fileError || kind === "invalid"
                ? "var(--text-error)"
                : "var(--text-secondary)"
            }
          >
            {status}
          </Text>
        )}
        <FieldActions>
          <FieldLink onPress={() => fileRef.current?.click()}>
            <Text variant="text-small">Upload .xml</Text>
          </FieldLink>
          <Text variant="text-small" color="var(--text-tertiary)">
            ·
          </Text>
          <FieldLink onPress={onEnterManually}>
            <Text variant="text-small">Enter manually</Text>
          </FieldLink>
        </FieldActions>
      </HelperRow>
      <input
        ref={fileRef}
        type="file"
        accept=".xml,application/xml,text/xml"
        hidden
        onChange={(e) => {
          void ingestFile(e.target.files?.[0]);
          e.target.value = "";
        }}
      />
    </Field>
  );
}

export function ManualSamlFields({
  config,
  onConfigChange,
  existing,
  onUseMetadata,
}: FieldsProps & { onUseMetadata: () => void }) {
  const isNew = !existing;
  const keepCurrent = existing ? "Leave blank to keep current" : undefined;
  const [certError, setCertError] = useState<string | null>(null);
  const [certDragging, setCertDragging] = useState(false);

  async function handleCertFile(files: FileList | null) {
    const file = files?.[0];
    if (!file) return;
    const text = await file.text();
    if (!looksLikePemCert(text)) {
      setCertError(
        "That file isn’t a Base64 certificate. Download the Certificate (Base64) version from your IdP."
      );
      return;
    }
    setCertError(null);
    onConfigChange((f) => ({ ...f, cert: text.trim() }));
  }

  function onCertDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setCertDragging(false);
    void handleCertFile(e.dataTransfer.files);
  }

  return (
    <>
      <ManualField
        label="IdP entity ID"
        action={
          <FieldLink onPress={onUseMetadata}>
            <Text variant="text-small">Use metadata</Text>
          </FieldLink>
        }
        hint="Checks that SAML responses come from your IdP"
      >
        <Input
          value={config.issuer}
          onChange={(e) =>
            onConfigChange((f) => ({ ...f, issuer: e.target.value }))
          }
          placeholder={isNew ? "https://idp.example.com/saml" : keepCurrent}
          required={isNew}
          style={{ width: "100%" }}
        />
      </ManualField>

      <ManualField
        label="Sign-on URL"
        hint="Your IdP’s single sign-on endpoint"
      >
        <Input
          value={config.entryPoint}
          onChange={(e) =>
            onConfigChange((f) => ({ ...f, entryPoint: e.target.value }))
          }
          placeholder={
            isNew ? "https://your-org.okta.com/app/sso/saml" : keepCurrent
          }
          required={isNew}
          style={{ width: "100%" }}
        />
      </ManualField>

      <ManualField
        label="IdP certificate"
        hint="Used to verify SAML responses"
        error={certError}
      >
        <DropZone
          data-dragging={certDragging}
          onDragOver={(e) => {
            e.preventDefault();
            setCertDragging(true);
          }}
          onDragLeave={() => setCertDragging(false)}
          onDrop={onCertDrop}
        >
          <CertTextarea
            value={config.cert}
            onChange={(e) => {
              setCertError(null);
              onConfigChange((f) => ({ ...f, cert: e.target.value }));
            }}
            placeholder={
              isNew
                ? "Paste the X.509 certificate (PEM), or drop a .cer file"
                : keepCurrent
            }
            required={isNew}
          />
        </DropZone>
      </ManualField>
    </>
  );
}
